import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * Toast system — replaces the inline `setMsg + setTimeout` pattern
 * repeated across every page. Wrap <App /> with <ToastProvider> and
 * call useToast() from any component.
 *
 *   const toast = useToast();
 *   toast.success('Saved');
 *   toast.error('Failed to save', 'Network timeout');
 *   toast.info('Re-syncing...');
 */

const ToastContext = createContext(null);

let _id = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  const push = useCallback((type, title, detail, duration = 3500) => {
    const id = ++_id;
    setToasts(t => [...t, { id, type, title, detail }]);
    if (duration > 0) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const api = {
    success: (t, d) => push('success', t, d),
    error:   (t, d) => push('error',   t, d, 5000),   // errors stay a bit longer
    info:    (t, d) => push('info',    t, d),
    warn:    (t, d) => push('warn',    t, d, 4500),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-stack">
        {toasts.map(t => <ToastItem key={t.id} toast={t} onDismiss={dismiss} />)}
      </div>
      <style>{CSS}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

function ToastItem({ toast, onDismiss }) {
  const icons = { success: '✅', error: '❌', info: 'ℹ️', warn: '⚠️' };
  return (
    <div className={`toast toast-${toast.type}`} role="status">
      <span className="toast-icon">{icons[toast.type]}</span>
      <div className="toast-body">
        <div className="toast-title">{toast.title}</div>
        {toast.detail && <div className="toast-detail">{toast.detail}</div>}
      </div>
      <button className="toast-close" onClick={() => onDismiss(toast.id)} aria-label="Dismiss">×</button>
    </div>
  );
}

const CSS = `
.toast-stack {
  position: fixed; bottom: 16px; right: 16px; z-index: 10000;
  display: flex; flex-direction: column; gap: 8px; max-width: 360px;
  pointer-events: none;
}
.toast {
  pointer-events: auto;
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 14px; border-radius: 8px;
  background: #fff; box-shadow: 0 6px 24px rgba(0,0,0,0.18);
  border-left: 4px solid #1a6fad;
  font-family: inherit; font-size: 13px;
  animation: toast-in 0.18s ease-out;
}
@keyframes toast-in {
  from { opacity: 0; transform: translateX(20px); }
  to   { opacity: 1; transform: translateX(0); }
}
.toast-success { border-left-color: #2d9e5f; }
.toast-error   { border-left-color: #e53e3e; }
.toast-info    { border-left-color: #4299e1; }
.toast-warn    { border-left-color: #f6a623; }
.toast-icon    { font-size: 16px; line-height: 1.2; flex-shrink: 0; }
.toast-body    { flex: 1; min-width: 0; }
.toast-title   { font-weight: 700; color: #1a2b3c; line-height: 1.4; }
.toast-detail  { font-size: 12px; color: #5a7286; margin-top: 2px; line-height: 1.4; }
.toast-close   {
  background: transparent; border: none; color: #8a9db0;
  font-size: 18px; cursor: pointer; padding: 0 4px; line-height: 1;
  align-self: flex-start;
}
.toast-close:hover { color: #1a2b3c; }
@media (max-width: 480px) {
  .toast-stack { left: 10px; right: 10px; bottom: 10px; max-width: none; }
}
`;
